import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { attemptsService } from '../services/attempts.service';
import { useTranslation } from '../../../i18n';

interface ResumeAttemptButtonProps {
  attemptId: string;
  scenarioId: string;
  canResume?: boolean;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const ResumeAttemptButton: React.FC<ResumeAttemptButtonProps> = ({
  attemptId,
  scenarioId,
  canResume = true,
  disabled = false,
  size = 'md',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResume = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canResume || disabled || resuming) return;

    setResuming(true);
    setError(null);
    try {
      await attemptsService.resumeAttempt(attemptId);
      navigate(`/simulations/${scenarioId}/run?attemptId=${attemptId}`);
    } catch (err: any) {
      setError(err?.message || t('attempts.err_resume_failed', undefined, 'Failed to resume attempt'));
      setResuming(false);
    }
  };

  const paddingStyle =
    size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 22px' : '9px 16px';
  const fontSize = size === 'sm' ? '12px' : size === 'lg' ? '14.5px' : '13.5px';

  if (!canResume) {
    return null;
  }

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <button
        type="button"
        onClick={handleResume}
        disabled={disabled || resuming}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: paddingStyle,
          fontSize: fontSize,
          fontWeight: 600,
          fontFamily: 'var(--body, sans-serif)',
          borderRadius: '9px',
          border: '1px solid #2563EB',
          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          color: '#FFFFFF',
          cursor: disabled || resuming ? 'not-allowed' : 'pointer',
          opacity: disabled || resuming ? 0.6 : 1,
          boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
          transition: 'all 0.15s ease',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        {resuming ? (
          <>
            <svg
              style={{ width: '15px', height: '15px', animation: 'spin 1s linear infinite' }}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{t('attempts.action.resuming', undefined, 'Resuming...')}</span>
          </>
        ) : (
          <>
            <svg
              style={{ width: '15px', height: '15px', flexShrink: 0 }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{t('attempts.action.resume_simulation', undefined, 'Resume Simulation')}</span>
          </>
        )}
      </button>
      {error && (
        <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: 'var(--bad, #EF4444)' }}>
          {error}
        </p>
      )}
    </div>
  );
};
